import {
  Component, Directive, ElementRef, OnDestroy, ViewChild, AfterViewInit,
  ViewContainerRef, TemplateRef, Renderer2, ComponentRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { Overlay, OverlayRef, ConnectedPosition } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { MatFormField } from '@angular/material/form-field';

// --- Expression Parser ---

interface Token {
  type: 'number' | 'op' | 'lparen' | 'rparen';
  value: string | number;
}

function tokenize(input: string): Token[] | null {
  const tokens: Token[] = [];
  let i = 0;
  while (i < input.length) {
    const ch = input[i];
    if (ch === ' ') {
      i++;
      continue;
    }
    if (ch === '(') {
      tokens.push({ type: 'lparen', value: '(' });
      i++;
    } else if (ch === ')') {
      tokens.push({ type: 'rparen', value: ')' });
      i++;
    } else if ('+-*/xX'.includes(ch)) {
      tokens.push({ type: 'op', value: 'xX'.includes(ch) ? '*' : ch });
      i++;
    } else if (/[0-9,.]/.test(ch)) {
      let num = '';
      while (i < input.length && /[0-9,.]/.test(input[i])) {
        num += input[i];
        i++;
      }
      // Danish: dots are thousand separators, comma is decimal separator
      const normalized = num.replace(/\./g, '').replace(',', '.');
      const parsed = parseFloat(normalized);
      if (isNaN(parsed)) return null;
      tokens.push({ type: 'number', value: parsed });
    } else {
      return null; // invalid character
    }
  }
  return tokens;
}

// Recursive descent parser with operator precedence
// expr     = term (('+' | '-') term)*
// term     = factor (('*' | '/') factor)*
// factor   = NUMBER | '(' expr ')' | ('+' | '-') factor

class Parser {
  private pos = 0;
  constructor(private tokens: Token[]) {}

  parse(): number | null {
    const result = this.expr();
    if (this.pos !== this.tokens.length) return null;
    return result;
  }

  private peek(): Token | null {
    return this.pos < this.tokens.length ? this.tokens[this.pos] : null;
  }

  private consume(): Token {
    return this.tokens[this.pos++];
  }

  private expr(): number | null {
    let left = this.term();
    if (left === null) return null;
    while (this.peek()?.type === 'op' && (this.peek()!.value === '+' || this.peek()!.value === '-')) {
      const op = this.consume().value;
      const right = this.term();
      if (right === null) return null;
      left = op === '+' ? left + right : left - right;
    }
    return left;
  }

  private term(): number | null {
    let left = this.factor();
    if (left === null) return null;
    while (this.peek()?.type === 'op' && (this.peek()!.value === '*' || this.peek()!.value === '/')) {
      const op = this.consume().value;
      const right = this.factor();
      if (right === null) return null;
      if (op === '/') {
        if (right === 0) return null;
        left = left / right;
      } else {
        left = left * right;
      }
    }
    return left;
  }

  private factor(): number | null {
    const token = this.peek();
    if (!token) return null;

    // Unary + or -
    if (token.type === 'op' && (token.value === '+' || token.value === '-')) {
      this.consume();
      const val = this.factor();
      if (val === null) return null;
      return token.value === '-' ? -val : val;
    }

    if (token.type === 'number') {
      this.consume();
      return token.value as number;
    }

    if (token.type === 'lparen') {
      this.consume();
      const val = this.expr();
      if (val === null) return null;
      if (this.peek()?.type !== 'rparen') return null;
      this.consume();
      return val;
    }

    return null;
  }
}

export function evaluateExpression(input: string): number | null {
  if (!input || !input.trim()) return null;
  const tokens = tokenize(input);
  if (!tokens || tokens.length === 0) return null;
  return new Parser(tokens).parse();
}

function isExpression(input: string): boolean {
  const tokens = tokenize(input);
  if (!tokens || tokens.length <= 1) return false;
  return tokens.some(t => t.type === 'op' || t.type === 'lparen' || t.type === 'rparen');
}

// --- Calculator Overlay Component ---

@Component({
    selector: 'app-calculator-overlay',
    templateUrl: './calculator.component.html',
    styleUrls: ['./calculator.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CalculatorOverlayComponent implements AfterViewInit {
  expression = '';
  result: number | null = null;
  lastValidResult: number | null = null;

  onApply: ((value: number) => void) | null = null;
  onDismiss: (() => void) | null = null;

  get displayValue(): number | null {
    return this.result ?? this.lastValidResult;
  }

  @ViewChild('expressionInput') expressionInput!: ElementRef<HTMLInputElement>;

  ngAfterViewInit() {
    setTimeout(() => this.expressionInput?.nativeElement.focus());
  }

  onExpressionChange() {
    const raw = evaluateExpression(this.expression);
    this.result = raw !== null ? Math.round(raw * 100) / 100 : null;
    if (this.result !== null) {
      this.lastValidResult = this.result;
    }
  }

  apply() {
    if (this.result !== null && this.onApply) {
      this.onApply(this.result);
    }
  }

  dismiss() {
    if (this.onDismiss) {
      this.onDismiss();
    }
  }
}

// --- Calculator Input Directive ---

@Directive({
    selector: '[calculatorInput]',
    standalone: false
})
export class CalculatorInputDirective implements AfterViewInit, OnDestroy {
  private overlayRef: OverlayRef | null = null;
  private suffixContainer: HTMLElement | null = null;

  private isCommitting = false;

  constructor(
    private overlay: Overlay,
    private elementRef: ElementRef,
    private formField: MatFormField,
    private renderer: Renderer2,
    private viewContainerRef: ViewContainerRef,
  ) {
    const el = this.elementRef.nativeElement as HTMLInputElement;

    el.addEventListener('blur', () => {
      if (this.isCommitting) return;
      const raw = el.value?.trim();
      if (!raw || !isExpression(raw)) return;
      const result = evaluateExpression(raw);
      if (result === null) return;
      const rounded = Math.round(result * 100) / 100;
      el.value = String(rounded).replace('.', ',');
    });

    el.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      const raw = el.value?.trim();
      if (!raw || !isExpression(raw)) return;
      const result = evaluateExpression(raw);
      if (result === null) return;
      const rounded = Math.round(result * 100) / 100;
      el.value = String(rounded).replace('.', ',');
    });
  }

  ngAfterViewInit() {
    // Defer to next microtask so Material has finished rendering the form field
    setTimeout(() => this.createSuffixButton());
  }

  private createSuffixButton() {
    const formFieldEl = this.formField._elementRef.nativeElement as HTMLElement;

    // Create the suffix container that Material expects
    const suffixContainer = this.renderer.createElement('div');
    this.renderer.addClass(suffixContainer, 'mat-mdc-form-field-icon-suffix');
    this.suffixContainer = suffixContainer;

    // Create a plain span with material-icons class (not a mat-icon component)
    const button = this.renderer.createElement('button');
    this.renderer.setAttribute(button, 'type', 'button');
    this.renderer.setAttribute(button, 'tabindex', '-1');
    this.renderer.setStyle(button, 'background', 'none');
    this.renderer.setStyle(button, 'border', 'none');
    this.renderer.setStyle(button, 'cursor', 'pointer');
    this.renderer.setStyle(button, 'padding', '0');
    this.renderer.setStyle(button, 'color', 'rgba(0,0,0,0.54)');
    this.renderer.setStyle(button, 'display', 'flex');
    this.renderer.setStyle(button, 'align-items', 'center');
    this.renderer.setStyle(button, 'line-height', '1');

    const icon = this.renderer.createElement('span');
    this.renderer.addClass(icon, 'material-icons');
    this.renderer.setStyle(icon, 'font-size', '20px');
    const iconText = this.renderer.createText('calculate');
    this.renderer.appendChild(icon, iconText);
    this.renderer.appendChild(button, icon);
    this.renderer.appendChild(suffixContainer, button);

    // Place it after the infix div inside .mat-mdc-form-field-flex
    const flexContainer = formFieldEl.querySelector('.mat-mdc-form-field-flex');
    if (flexContainer) {
      this.renderer.appendChild(flexContainer, suffixContainer);
    }

    this.renderer.listen(button, 'click', (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      this.toggleOverlay();
    });
  }

  private toggleOverlay() {
    if (this.overlayRef?.hasAttached()) {
      this.close();
      return;
    }

    this.open();
  }

  private open() {
    const positions: ConnectedPosition[] = [
      { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: -16 },
      { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
    ];

    this.overlayRef = this.overlay.create({
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.formField._elementRef)
        .withPositions(positions),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    });

    this.overlayRef.backdropClick().subscribe(() => this.close());

    const portal = new ComponentPortal(CalculatorOverlayComponent, this.viewContainerRef);
    const ref: ComponentRef<CalculatorOverlayComponent> = this.overlayRef.attach(portal);

    // Pre-fill with whatever is currently in the input (even uncommitted text)
    const rawValue = (this.elementRef.nativeElement as HTMLInputElement).value?.trim();
    if (rawValue) {
      ref.instance.expression = rawValue;
      ref.instance.onExpressionChange();
    }

    ref.instance.onApply = (value: number) => {
      const rounded = Math.round(value * 100) / 100;
      this.close();
      this.commitValue(rounded);
    };

    ref.instance.onDismiss = () => {
      this.close();
    };

    ref.changeDetectorRef.detectChanges();
  }

  private commitValue(value: number) {
    // Set the input value with comma decimal and let CurrencyFormatterDirective
    // handle the model update and display formatting via its blur handler
    const el = this.elementRef.nativeElement as HTMLInputElement;
    el.value = String(value).replace('.', ',');
    this.isCommitting = true;
    el.focus();
    el.blur();
    this.isCommitting = false;
  }

  private close() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  ngOnDestroy() {
    this.close();
    if (this.suffixContainer?.parentNode) {
      this.renderer.removeChild(this.suffixContainer.parentNode, this.suffixContainer);
    }
  }
}
